import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { tokenStore, purgeLegacyTokenStorage } from '../common/services/tokenStore';
import { refreshAccessToken } from '../common/services/authRefresh';

interface User {
  user_id: number;
  email: string;
  username: string;
  full_name?: string;
  role: string;
  is_active: boolean;
  created_at: string;
  organisation_id?: string;
  language: string;
  avatar_url?: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  updateLanguage: (language: string) => Promise<void>;
  updateAvatar: (dataUrl: string) => Promise<void>;
  removeAvatar: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_BASE_URL || (import.meta as any).env?.VITE_API_URL || 'http://localhost:1245';

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${apiUrl}/api/v1/auth/login`, {
        email,
        password,
      });
      // Only the short-lived access token is kept, in memory. The refresh
      // token arrives as an httpOnly cookie and is never exposed to JS
      // (issue #6).
      const { access_token } = response.data;
      tokenStore.set(access_token);

      const userResponse = await axios.get(`${apiUrl}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      setUser(userResponse.data);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // The refresh token lives in the httpOnly cookie and is revoked
      // server-side; no token is passed in the body.
      await axios.post(
        `${apiUrl}/api/v1/auth/logout`,
        undefined,
        {
          headers: {
            Authorization: `Bearer ${tokenStore.get()}`,
          },
        }
      );
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      tokenStore.clear();
      sessionStorage.removeItem('admin_redirect_in_progress');
      sessionStorage.removeItem('demo_redirect_in_progress');
      setUser(null);
    }
  };

  const refreshToken = async () => {
    try {
      // Silently exchange the httpOnly refresh cookie for a fresh access
      // token. No refresh token is (or can be) read by this app. Goes
      // through the shared, deduped refreshAccessToken() so this never races
      // a concurrent refresh triggered by request.ts's 401 handler — the
      // refresh cookie is single-use and a race would trip the backend's
      // reuse-detection and log the user out of every session.
      const accessToken = await refreshAccessToken();
      if (!accessToken) {
        throw new Error('Token refresh failed');
      }

      const userResponse = await axios.get(`${apiUrl}/api/v1/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUser(userResponse.data);
    } catch (error) {
      console.error('Token refresh failed:', error);
      tokenStore.clear();
      setUser(null);
      throw error;
    }
  };

  const updateLanguage = async (language: string) => {
    const response = await axios.put(
      `${apiUrl}/api/v1/auth/me`,
      { language },
      { headers: { Authorization: `Bearer ${tokenStore.get()}` } }
    );
    setUser(response.data);
  };

  const updateAvatar = async (dataUrl: string) => {
    const response = await axios.put(
      `${apiUrl}/api/v1/auth/me`,
      { avatar_url: dataUrl },
      { headers: { Authorization: `Bearer ${tokenStore.get()}` } }
    );
    setUser(response.data);
  };

  const removeAvatar = async () => {
    const response = await axios.put(
      `${apiUrl}/api/v1/auth/me`,
      { avatar_url: null },
      { headers: { Authorization: `Bearer ${tokenStore.get()}` } }
    );
    setUser(response.data);
  };

  // Set up the axios interceptor for token refresh, then bootstrap auth.
  // The interceptor is the single owner of all 401 -> refresh -> retry logic,
  // so it must be registered before initAuth fires its first /auth/me request.
  useEffect(() => {
    // All bare-axios calls in the app must send/receive the httpOnly refresh
    // cookie (cross-origin cookies require explicit opt-in).
    axios.defaults.withCredentials = true;
    // Remove tokens persisted by older versions (issue #6).
    purgeLegacyTokenStorage();

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't retry if:
        // 1. Already retried
        // 2. The failing request IS the refresh endpoint (prevents infinite loop)
        // 3. The failing request IS the login endpoint
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/login')
        ) {
          originalRequest._retry = true;
          try {
            await refreshToken();
            const newAccessToken = tokenStore.get();
            if (!newAccessToken) {
              return Promise.reject(error);
            }
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // Clear token and user on refresh failure
            tokenStore.clear();
            setUser(null);
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    const initAuth = async () => {
      // The access token lives only in memory, so after a page reload we
      // silently re-authenticate via the httpOnly refresh cookie first.
      if (!tokenStore.get()) {
        try {
          await refreshToken();
        } catch (error) {
          // Not authenticated — the login page will handle it.
          console.debug('Not authenticated on load:', error);
        }
      } else {
        try {
          // If the access token is expired, this 401s and the
          // interceptor transparently refreshes and retries before resolving.
          const response = await axios.get(`${apiUrl}/api/v1/auth/me`, {
            headers: { Authorization: `Bearer ${tokenStore.get()}` },
          });
          setUser(response.data);
        } catch (error) {
          // Reaching here means auth genuinely failed (no/invalid refresh
          // token); the interceptor has already cleared tokens and user.
          console.debug('Not authenticated on load:', error);
        }
      }
      setLoading(false);
    };

    initAuth();

    // A refresh failure triggered from outside this file (e.g. request.ts's
    // own 401 handler, which shares refreshAccessToken()) still needs to
    // clear `user` here so ProtectedRoute redirects to /login.
    const handleRefreshFailed = () => setUser(null);
    window.addEventListener('auth:refresh-failed', handleRefreshFailed);

    return () => {
      axios.interceptors.response.eject(interceptor);
      window.removeEventListener('auth:refresh-failed', handleRefreshFailed);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, refreshToken, updateLanguage, updateAvatar, removeAvatar }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
